import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Navbar from '../components/Navbar';
import ConvertUploader from '../components/Convert/ConvertUploader';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { EPaymentType, SetModalPayment } from '../store/layout.slice';
import PaymentModal from '../components/Modals/Payment.modal';
import { EUploadStatus } from '../@types/Upload';
import { CheckConvertTypeCompatible, IsValidEmail } from '../utils/validator';
import { SetConvertType, SetUploadStatus, SetUploadedUrl } from '../store/convert.page.slice';
import { EConvertType, IConvertConfig } from '../@types/Convert';
import SrtToFCPXML from '../components/Convert/Config/SrtToFCPXML';
import SrtToAvidSubCap from '../components/Convert/Config/SrtToAvidSubCap';
import SrtToEDL from '../components/Convert/Config/SrtToEDL';

function Convert() {
    const dispatch = useAppDispatch();

    const [searchParams, setSearchParams] = useSearchParams();

    const uploadStatus = useAppSelector((state) => state.pageConvert.uploader.status);
    const uploader = useAppSelector((state) => state.pageConvert.uploader);
    const convertType = useAppSelector((state) => state.pageConvert.convertConfig.type);
    
    const refConvertType = useRef<HTMLSelectElement>(null);
    const refEmail = useRef<HTMLInputElement>(null);

    const [convertConfig, setConvertConfig] = useState<IConvertConfig>({ width: 0, height: 0, fps: 0 });

    useEffect(() => {
        dispatch(SetUploadStatus(EUploadStatus.NoFileSelected));
        dispatch(SetUploadedUrl(''));

        let defaultType: EConvertType = searchParams.get('type') as EConvertType || EConvertType.SrtToFCPXML;

        // Set default convert type
        if(refConvertType.current) refConvertType.current.value = defaultType
        dispatch(SetConvertType(defaultType));
    }, [])

    const onChangeConvertType = (event: any) => {
        dispatch(SetConvertType(event.target.value as EConvertType));
    }

    const onSettingSrtToFCPXMLChange = useCallback((config: IConvertConfig) => { setConvertConfig(config); }, []);
    const onSettingSrtToAvidSubCapChange = useCallback((config: IConvertConfig) => { setConvertConfig(config); }, []);
    
    const onClickCheckout = async () => {
        if(uploadStatus !== EUploadStatus.Uploaded) {
            alert(`No file is uploaded.`);
            return;
        }

        // Check selected convert type and file convert type compatible in term of file upload?
        if(!CheckConvertTypeCompatible(convertType, uploader.file.convertType)) {
            alert(`Uploaded file and selected convert type are not compatible.`);
            return;
        }

        let email = refEmail.current?.value || '';
        if(email.length <= 0 || !IsValidEmail(email)) { refEmail.current?.focus(); return; }

        switch(refConvertType.current?.value) {
            case EConvertType.SrtToFCPXML: {
                if(!convertConfig.width || convertConfig.width < 100 || convertConfig.width > 9999) { 
                    alert("Media's width is invalid");
                    return; 
                }
                if(!convertConfig.height || convertConfig.height < 100 || convertConfig.height > 9999) { 
                    alert("Media's height is invalid");
                    return; 
                }
            
                dispatch(SetModalPayment({ show: true, 
                    type: EPaymentType.Convert, 
                    convertType: EConvertType.SrtToFCPXML,
                    data: {
                        email,
                        fileName: uploader.file.name,
                        fileUrl: uploader.uploadedUrl,
                        videoWidth: convertConfig.width,
                        videoHeight: convertConfig.height,
                        videoFPS: convertConfig.fps
                    } 
                }));

                break;
            }
            case EConvertType.XMLToFCPXML: {
                dispatch(SetModalPayment({ show: true, 
                    type: EPaymentType.Convert, 
                    convertType: EConvertType.XMLToFCPXML,
                    data: {
                        email,
                        fileName: uploader.file.name,
                        fileUrl: uploader.uploadedUrl
                    } 
                }));

                break;
            }
            case EConvertType.SrtToAvidSubCap: {            
                dispatch(SetModalPayment({ show: true, 
                    type: EPaymentType.Convert, 
                    convertType: EConvertType.SrtToAvidSubCap,
                    data: {
                        email,
                        fileName: uploader.file.name,
                        fileUrl: uploader.uploadedUrl,
                        videoFPS: convertConfig.fps
                    } 
                }));

                break;
            }
            case EConvertType.SrtToEDL: {            
                dispatch(SetModalPayment({ show: true, 
                    type: EPaymentType.Convert, 
                    convertType: EConvertType.SrtToEDL,
                    data: {
                        email,
                        fileName: uploader.file.name,
                        fileUrl: uploader.uploadedUrl,
                        videoFPS: convertConfig.fps
                    } 
                }));

                break;
            }
            default: break;
        }
    }

    var renderConvertConfig = <></>;
    var renderPrice = <></>;

    switch(convertType) {
        case EConvertType.SrtToFCPXML: 
            renderConvertConfig = <SrtToFCPXML onSettingChange={onSettingSrtToFCPXMLChange}/>; 
            renderPrice = <span>5</span>;
            break;
        case EConvertType.XMLToFCPXML:
            renderPrice = <span>2</span>;
            break;
        case EConvertType.SrtToAvidSubCap:
            renderConvertConfig = <SrtToAvidSubCap onSettingChange={onSettingSrtToAvidSubCapChange}/>; 
            renderPrice = <span>5</span>;
            break;
        case EConvertType.SrtToEDL:
            renderConvertConfig = <SrtToEDL onSettingChange={onSettingSrtToAvidSubCapChange}/>; 
            renderPrice = <span>5</span>;
            break;
        default: break;
    }

    return (
        <div>
            {/* <Navbar page="convert"/> */ }
            <div className='anim-fade-in max-w-5xl mx-auto pt-4 md:pt-10 px-5 pb-20'>
                {/* 
                <h3 className='text-4xl md:text-5xl font-bold tracking-wide mb-1 md:mb-2 text-center'>Convert Subtitle</h3>
                <p className='text-2xl mb-10 md:mb-20 text-gray-700 text-center'>Convert your subtitles into different formats.</p>*/}
                <ConvertUploader/>
                <div className='grid grid-cols-2 gap-4 mb-5'>
                    <div>
                        <label htmlFor="convertType" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Convert</label>
                        <select ref={refConvertType} onChange={onChangeConvertType} id="convertType" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
                            <option value={EConvertType.SrtToFCPXML} selected>SRT to FCPXML Titles</option>
                            <option value={EConvertType.XMLToFCPXML} disabled>XML to FCPXML (Coming soon)</option>
                            <option value={EConvertType.SrtToAvidSubCap}>SRT to Avid SubCap</option>
                            <option value={EConvertType.SrtToWebVTT} disabled>SRT to WebVTT (Coming soon)</option>
                            <option value={EConvertType.WebVTTToSrt} disabled>WebVTT to SRT (Coming soon)</option>
                            <option value={EConvertType.FCPXMLToXML} disabled>FCPXML to XML (Coming soon)</option>
                            <option value={EConvertType.SrtToEDL}>SRT to EDL</option>
                        </select>
                    </div>
                    <div>
                        <label data-tooltip-target="tooltip-email" htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Email Address<i className="fa-solid fa-circle-info ml-1.5"></i></label>
                        <input ref={refEmail} type="email" id="email" className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="you@mail.com" required/>
                        <div id="tooltip-email" role="tooltip" className="absolute z-10 invisible inline-block px-3 py-2 text-sm font-medium text-white transition-opacity duration-300 bg-gray-900 rounded-lg shadow-sm opacity-0 tooltip dark:bg-gray-700">
                            Converted file will be sent to your email, make sure it's correct :)
                            <div className="tooltip-arrow" data-popper-arrow></div>
                        </div>
                    </div>
                </div>
                { renderConvertConfig }
                <hr className="w-48 h-1 mx-auto my-4 bg-gray-100 border-0 rounded md:my-10 dark:bg-gray-700 mb-5"></hr>
                <div className='flex justify-between items-center flex-row p-6 bg-white border border-gray-200 rounded-lg shadow'>
                    <div>
                        <p className='text-gray-500 mb-0 leading-4'>Total Cost</p>
                        <p>
                            <span className="text-xl font-semibold">$</span>
                            <span className="text-2xl font-bold tracking-tight">{ renderPrice }</span>
                        </p>
                    </div>
                    <button onClick={onClickCheckout} className="inline-flex items-center px-4 py-2 text-sm font-medium text-center text-white bg-gray-800 rounded-lg hover:bg-gray-900 active:bg-gray-900"><i className="fa-solid fa-cart-shopping mr-1.5"></i>Checkout</button>
                </div>
            </div>
            <PaymentModal/>
        </div>
    )
}

export default React.memo(Convert);